import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../api';

const updateAdminLimit = (payload) => apiPost('/api/transaction/admin-limit', payload);
const setAdminPermissions = (payload) => apiPost('/api/admin/permissions', payload);

// Balances are stripped from the API response without 'viewBalances', so an
// admin handed 'editBalance' alone would open the edit form on a blank figure.
// Turning one on turns the other on with it.
const BALANCE_VIEW = 'viewBalances';
const BALANCE_EDIT = 'editBalance';

const DarkModal = ({ show, onClose, children, maxWidth = 440 }) => {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}>
            <div style={{
                background: '#13132a',
                border: '1px solid rgba(255,77,109,0.2)',
                borderRadius: 18, padding: '28px 24px',
                width: '90%', maxWidth,
                maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                position: 'relative'
            }}>
                <button onClick={onClose} style={{
                    position: 'absolute', top: 14, right: 18,
                    background: 'none', border: 'none',
                    color: 'rgba(255,255,255,0.4)', fontSize: 20,
                    cursor: 'pointer', lineHeight: 1
                }}>×</button>
                {children}
            </div>
        </div>
    );
};

const AllAdmins = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [newLimit, setNewLimit] = useState('');
    const [status, setStatus] = useState({ type: 'idle', message: '' });

    // Permission editor. The catalog (key, label, group) is served by the
    // backend so this screen and the gates it controls can't drift apart.
    const [catalog, setCatalog] = useState([]);
    const [permAdmin, setPermAdmin] = useState(null);
    const [permDraft, setPermDraft] = useState({});
    const [permStatus, setPermStatus] = useState({ type: 'idle', message: '' });

    const fetchAdmins = useCallback(async () => {
        try {
            setAdmins(await apiGet('/api/admin/accounts/admins'));
        } catch {
            setError('Failed to load admins.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

    useEffect(() => {
        // A failure here only costs the editor its labels, so the limit half of
        // the screen still works — no page-level error for it.
        apiGet('/api/admin/permissions/catalog').then(setCatalog).catch(() => setCatalog([]));
    }, []);

    const openModal = (admin) => {
        setSelectedAdmin(admin);
        setNewLimit(admin.sendLimit?.toString() || '');
        setStatus({ type: 'idle', message: '' });
    };

    const closeModal = () => {
        setSelectedAdmin(null);
        setNewLimit('');
        setStatus({ type: 'idle', message: '' });
    };

    const openPermissions = (admin) => {
        setPermAdmin(admin);
        // Fall back to the catalog defaults for an admin created before a key
        // existed — the server fills the same gaps the same way.
        const stored = admin.permissions || {};
        const draft = {};
        catalog.forEach(({ key, defaultValue }) => {
            draft[key] = stored[key] === undefined ? defaultValue === true : stored[key] === true;
        });
        setPermDraft(draft);
        setPermStatus({ type: 'idle', message: '' });
    };

    const closePermissions = () => {
        setPermAdmin(null);
        setPermDraft({});
        setPermStatus({ type: 'idle', message: '' });
    };

    const togglePermission = (key) => {
        setPermDraft(prev => {
            const next = { ...prev, [key]: !prev[key] };
            // Keep the balance pair coherent: editing implies seeing.
            if (key === BALANCE_EDIT && next[BALANCE_EDIT]) next[BALANCE_VIEW] = true;
            if (key === BALANCE_VIEW && !next[BALANCE_VIEW]) next[BALANCE_EDIT] = false;
            return next;
        });
    };

    const handleSavePermissions = async () => {
        setPermStatus({ type: 'loading', message: 'Saving permissions...' });
        try {
            const res = await setAdminPermissions({ targetAdminUid: permAdmin.id, permissions: permDraft });
            if (res.success === false) {
                setPermStatus({ type: 'error', message: res.message });
                return;
            }
            setPermStatus({ type: 'success', message: 'Permissions saved.' });
            fetchAdmins();
        } catch (err) {
            setPermStatus({ type: 'error', message: err.message });
        }
    };

    const handleSetLimit = async (e) => {
        e.preventDefault();
        const parsed = parseInt(newLimit, 10);
        if (isNaN(parsed) || parsed < 0) {
            setStatus({ type: 'error', message: 'Valid positive number required.' });
            return;
        }
        setStatus({ type: 'loading', message: 'Updating limit...' });
        try {
            const res = await updateAdminLimit({ targetAdminUid: selectedAdmin.id, newLimit: parsed });
            if (res.success === false) {
                setStatus({ type: 'error', message: res.message });
                return;
            }
            setStatus({ type: 'success', message: `Limit set to ${parsed} successfully.` });
            fetchAdmins();
        } catch (err) {
            setStatus({ type: 'error', message: err.message });
        }
    };

    if (loading) return (
        <div className="dark-loading">
            <div className="spinner" />
            <p>Loading Admins...</p>
        </div>
    );

    if (error) return <div className="dark-error">{error}</div>;

    return (
        <div className="dark-page">
            <h2>Manage Admins</h2>

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Email</th>
                            <th className="text-right">Send Limit</th>
                            <th className="text-right">Remaining</th>
                            <th className="text-right">Total Sent</th>
                            <th className="text-center">Permissions</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {admins.map(admin => (
                            <tr key={admin.id}>
                                <td className="font-medium">{admin.name}</td>
                                <td>
                                    <span style={{
                                        fontSize: 10, fontWeight: 700,
                                        padding: '2px 8px', borderRadius: 20,
                                        background: admin.role === 'superadmin'
                                            ? 'rgba(168,85,247,0.15)' : 'rgba(255,77,109,0.15)',
                                        color: admin.role === 'superadmin' ? '#c084fc' : '#ff4d6d',
                                        border: `1px solid ${admin.role === 'superadmin' ? 'rgba(168,85,247,0.3)' : 'rgba(255,77,109,0.25)'}`,
                                        textTransform: 'capitalize'
                                    }}>
                                        {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                                    </span>
                                </td>
                                <td className="text-muted">{admin.email}</td>
                                <td className="text-right">
                                    {admin.role === 'superadmin'
                                        ? <span style={{ color: '#c084fc' }}>Unlimited</span>
                                        : <span style={{ color: '#fff' }}>{admin.sendLimit?.toLocaleString() ?? '—'}</span>
                                    }
                                </td>
                                <td className="text-right">
                                    {admin.role === 'superadmin'
                                        ? <span style={{ color: '#c084fc' }}>Unlimited</span>
                                        : <span style={{ color: admin.remainingLimit > 0 ? '#22c55e' : '#f87171' }}>
                                            {admin.remainingLimit?.toLocaleString() ?? '—'}
                                          </span>
                                    }
                                </td>
                                <td className="text-right text-muted">
                                    {admin.totalSent?.toLocaleString() ?? '0'}
                                </td>
                                <td className="text-center">
                                    {admin.role === 'superadmin'
                                        ? <span style={{ color: '#c084fc' }}>All</span>
                                        : <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                                            {Object.values(admin.permissions || {}).filter(Boolean).length}
                                            {' / '}
                                            {catalog.length || Object.keys(admin.permissions || {}).length}
                                          </span>
                                    }
                                </td>
                                <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                    {admin.role !== 'superadmin' && (
                                        <>
                                            <button
                                                className="btn-action"
                                                style={{ marginRight: 6, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' }}
                                                onClick={() => openModal(admin)}
                                                title="Set Coin Limit"
                                            >
                                                <i className="fas fa-sliders-h" />
                                            </button>
                                            <button
                                                className="btn-action"
                                                style={{ background: 'rgba(168,85,247,0.15)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.3)' }}
                                                onClick={() => openPermissions(admin)}
                                                title="Permissions"
                                            >
                                                <i className="fas fa-user-lock" />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {admins.length === 0 && (
                            <tr className="empty-row"><td colSpan="8">No admins found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <DarkModal show={!!selectedAdmin} onClose={closeModal}>
                <h2 style={{ color: '#fff', marginBottom: 4 }}>Set Coin Limit</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 20 }}>
                    Admin: <strong style={{ color: '#fff' }}>{selectedAdmin?.name}</strong>
                    {selectedAdmin?.remainingLimit !== undefined && (
                        <> &nbsp;·&nbsp; Current remaining: <strong style={{ color: '#22c55e' }}>{selectedAdmin.remainingLimit?.toLocaleString()}</strong></>
                    )}
                </p>

                {status.type === 'success' ? (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(34,197,94,0.12)', border: '2px solid rgba(34,197,94,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px'
                        }}>
                            <i className="fas fa-check" style={{ fontSize: 22, color: '#22c55e' }} />
                        </div>
                        <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 20 }}>{status.message}</p>
                        <button className="signupbtn" style={{ width: '100%', background: '#22c55e' }} onClick={closeModal}>Done</button>
                    </div>
                ) : (
                    <form onSubmit={handleSetLimit} className="accinput">
                        {status.type === 'error' && (
                            <div style={{
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#f87171', fontSize: 13
                            }}>
                                <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />
                                {status.message}
                            </div>
                        )}
                        <label>New Coin Limit</label>
                        <input
                            type="number"
                            value={newLimit}
                            onChange={e => setNewLimit(e.target.value)}
                            placeholder="e.g. 50000"
                            min="0"
                            required
                            disabled={status.type === 'loading'}
                        />
                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: -8, marginBottom: 16 }}>
                            This will reset the remaining limit to this value.
                        </p>
                        <div className="clearfix">
                            <button type="button" className="cancelbtn" onClick={closeModal} disabled={status.type === 'loading'}>Cancel</button>
                            <button type="submit" className="signupbtn" disabled={status.type === 'loading'}>
                                {status.type === 'loading'
                                    ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Updating...</>
                                    : 'Set Limit'
                                }
                            </button>
                        </div>
                    </form>
                )}
            </DarkModal>

            {/* ── Permissions ────────────────────────────────────────────── */}
            <DarkModal show={!!permAdmin} onClose={closePermissions} maxWidth={560}>
                <h2 style={{ color: '#fff', marginBottom: 4 }}>Permissions</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 6 }}>
                    Admin: <strong style={{ color: '#fff' }}>{permAdmin?.name}</strong>
                </p>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
                    Anything switched off disappears from their panel. Creating admins, this
                    screen, and sending coins beyond their limit stay with the Super Admin and
                    cannot be handed over.
                </p>

                {permStatus.type === 'error' && (
                    <div style={{
                        background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#f87171', fontSize: 13
                    }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />
                        {permStatus.message}
                    </div>
                )}
                {permStatus.type === 'success' && (
                    <div style={{
                        background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                        borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#22c55e', fontSize: 13
                    }}>
                        <i className="fas fa-check-circle" style={{ marginRight: 8 }} />
                        {permStatus.message}
                    </div>
                )}

                {catalog.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                        Could not load the permission list. Reload the page and try again.
                    </p>
                ) : (
                    [...new Set(catalog.map(p => p.group))].map(group => (
                        <div key={group} style={{ marginBottom: 18 }}>
                            <p style={{
                                fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                                color: 'rgba(255,255,255,0.35)', margin: '0 0 8px'
                            }}>{group}</p>

                            {catalog.filter(p => p.group === group).map(({ key, label, description }) => (
                                <label
                                    key={key}
                                    style={{
                                        display: 'flex', alignItems: 'flex-start', gap: 12,
                                        padding: '10px 12px', marginBottom: 6, borderRadius: 10,
                                        cursor: permStatus.type === 'loading' ? 'default' : 'pointer',
                                        background: permDraft[key] ? 'rgba(168,85,247,0.10)' : 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${permDraft[key] ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.07)'}`
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={permDraft[key] === true}
                                        onChange={() => togglePermission(key)}
                                        disabled={permStatus.type === 'loading'}
                                        style={{ marginTop: 3, width: 16, height: 16, accentColor: '#a855f7', flexShrink: 0 }}
                                    />
                                    <span>
                                        <span style={{ color: '#fff', fontSize: 14, fontWeight: 600, display: 'block' }}>{label}</span>
                                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, lineHeight: 1.45 }}>{description}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    ))
                )}

                <div className="clearfix">
                    <button type="button" className="cancelbtn" onClick={closePermissions} disabled={permStatus.type === 'loading'}>
                        Close
                    </button>
                    <button
                        type="button"
                        className="signupbtn"
                        style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}
                        onClick={handleSavePermissions}
                        disabled={permStatus.type === 'loading' || catalog.length === 0}
                    >
                        {permStatus.type === 'loading'
                            ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Saving...</>
                            : 'Save Permissions'
                        }
                    </button>
                </div>
            </DarkModal>
        </div>
    );
};

export default AllAdmins;
